import React from 'react';
import { Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    container: {
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'middle',
        cursor: 'pointer',
        maxWidth: (props: { maxWidth?: number; fixedWidth?: number }) => props.fixedWidth ? `${props.fixedWidth}px` : (props.maxWidth || 200),
        width: (props: { maxWidth?: number; fixedWidth?: number }) => props.fixedWidth ? `${props.fixedWidth}px` : 'auto',
        overflow: 'hidden',
    },
    text: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        fontWeight: 'normal',
        flexShrink: 1,
        textOverflow: 'clip',
    },
    ellipsis: {
        fontWeight: 'bold',
        flexShrink: 0,
    },
    tooltip: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        maxWidth: 400,
        border: `1px solid ${theme.palette.primary.main}`,
    },
}));

interface TruncatedTextProps {
    text: string;
    maxLength?: number;
    maxWidth?: number;
    fixedWidth?: number; // Fixed width in pixels
}

const TruncatedText: React.FC<TruncatedTextProps> = ({ text, maxLength = 30, maxWidth, fixedWidth }) => {
    const classes = useStyles({ maxWidth, fixedWidth });
    const textRef = React.useRef<HTMLSpanElement>(null);
    const [isWidthTruncated, setIsWidthTruncated] = React.useState(false);
    
    const isCharLong = text && text.length > maxLength;

    React.useEffect(() => {
        if (textRef.current) {
            const { scrollWidth, offsetWidth } = textRef.current;
            setIsWidthTruncated(scrollWidth > offsetWidth);
        }
    }, [text, fixedWidth, maxWidth]);

    if (!text) return <></>;

    const showEllipsis = isCharLong || isWidthTruncated;
    const displayText = isCharLong ? text.substring(0, maxLength) : text;

    const content = (
        <div className={classes.container}>
            <span ref={textRef} className={classes.text}>
                {displayText}
            </span>
            {showEllipsis && <span className={classes.ellipsis}>...</span>}
        </div>
    );

    if (showEllipsis || fixedWidth) {
        return (
            <Tooltip
                arrow
                classes={{ tooltip: classes.tooltip }}
                title={<Typography color="inherit" sx={{ fontWeight: 'normal' }}>{text}</Typography>}
            >
                {content}
            </Tooltip>
        );
    }

    return <span style={{ fontWeight: 'normal' }}>{text}</span>;
};

export default TruncatedText;
